import { createClient } from '@supabase/supabase-js';
import {
    BUILD_GUARD_STATUS,
    istBuildRiegelAntwort,
    meldeBuildRiegel,
} from './versionGate';

let supabaseInstance: any = null;

/**
 * Der Build-Stempel dieses Bundles — er geht als `x-app-build` an JEDEN
 * Request des Clients.
 *
 * Wozu: der serverseitige Build-Riegel (Trigger auf `projects` und zehn
 * Kindtabellen) weist Schreibvorgaenge angemeldeter Nutzer ab, deren Stempel
 * fehlt oder aelter ist als `app_maintenance.min_build`. Damit greift die
 * Regel auch dort, wo der Client sie selbst nicht mehr durchsetzen kann —
 * ein Tab mit totem Netzpfad zu /version.json haelt sich fuer aktuell und
 * schreibt sonst weiter (07.09.2026).
 *
 * `__BUILD_TIME__` biegt Vite beim Bauen auf `globalThis.__BUILD_TIME__` um,
 * gesetzt von einem Inline-Skript in index.html (vite.config.ts,
 * 'inject-build-globals'). Fehlt dieses Skript oder laeuft ein Test ohne den
 * define-Block, waere der Wert nicht definiert — deshalb der
 * `typeof`-Schutz und der Rueckfall auf den Zeitpunkt des Modulstarts: der
 * Header ist NIE leer, denn ein leerer Stempel wird vom Riegel wie ein
 * fehlender behandelt.
 *
 * Einmal beim Modulstart berechnet: ein Header, der sich zwischen zwei
 * Requests aendert, waere kein Stempel mehr.
 */
export const APP_BUILD_HEADER = 'x-app-build';

/**
 * Aus dem Rohwert einen Stempel machen, den der Server sicher lesen kann.
 *
 * Der Riegel castet die Kopfzeile nach `timestamptz` und faellt bei allem,
 * was er nicht lesen kann, auf 1970 zurueck — also auf „veraltet". Ein
 * kaputter Stempel waere damit genauso schlimm wie gar keiner. Deshalb wird
 * hier NICHT durchgereicht, sondern normalisiert: `toISOString()` liefert
 * immer die Zulu-Form `YYYY-MM-DDTHH:MM:SS.sssZ`.
 *
 * Rueckfall auf „jetzt", wenn der Rohwert fehlt, leer oder unlesbar ist —
 * das ist der Entwicklungslauf und der Testlauf ohne den define-Block von
 * Vite. Nie leer, nie unlesbar.
 */
export const baueBuildStempel = (rohwert?: string | null): string => {
    const roh = typeof rohwert === 'string' ? rohwert.trim() : '';
    if (roh !== '') {
        const ms = Date.parse(roh);
        if (Number.isFinite(ms)) return new Date(ms).toISOString();
    }
    return new Date().toISOString();
};

/**
 * Der Stempel dieses Laufs — einmal beim Modulstart berechnet.
 *
 * Ein Header, der sich zwischen zwei Requests aendert, waere kein Stempel
 * mehr. Exportiert, damit Anzeige und Regeln denselben Wert verwenden wie
 * die Kopfzeile: `__BUILD_TIME__` direkt zu lesen hiesse, im
 * Entwicklungslauf einen anderen Wert zu zeigen als zu senden.
 */
export const aktuellerBuildStempel: string =
    baueBuildStempel(typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : undefined);

/**
 * Die Kopfzeilen dieses Clients.
 *
 * `X-Client-Info` steht hier von Hand, und das ist kein Schmuck:
 * `applySettingDefaults` mischt `global` FLACH (helpers.js:27
 * `Object.assign({}, DEFAULT_GLOBAL_OPTIONS, globalOptions)`), ein eigenes
 * `headers`-Objekt ERSETZT also die Vorgabe vollstaendig statt sie zu
 * ergaenzen. Ohne diese Zeile verschwaende der Client seine Kennung — die
 * Supabase-Protokolle wuerden alle Requests dieser App als unbekannt
 * fuehren.
 *
 * Wert und Form stammen aus `@supabase/supabase-js/dist/module/lib/
 * constants.js:16` (`supabase-js-${JS_ENV}/${version}`). Die Version ist in
 * package.json EXAKT gepinnt (`"@supabase/supabase-js": "2.47.10"`, kein
 * Caret) — ein Hochziehen ist damit ein bewusster Schritt, bei dem diese
 * Zeile mitgeht. Die Umgebung ist hier immer 'web'; im tsx-Lauf eines
 * Skripts stuende sonst 'node', was fuer eine Kennung folgenlos ist.
 */
export const CLIENT_INFO_HEADER_VALUE = 'supabase-js-web/2.47.10';

/** So viel von einer `fetch`-Hülle, wie hier gebraucht wird — ohne DOM-Typen. */
type FetchAehnlich = (input: any, init?: any) => Promise<any>;

/**
 * Die Gegenrichtung des Stempels: EIN Haken für alle Schreibwege.
 *
 * Der Riegel weist mit HTTP 400 und dem Signalwort `BUILD_VERALTET` ab. Bis
 * hierher musste jeder Schreibweg das selbst erkennen — und die Hälfte tat es
 * nicht (`saveReportSnapshot`, `restoreInspectionItems`, `applyLinkTransfer`,
 * `projectLinkService`, Journal-Spiegel). Sie alle einzeln umzubauen hiesse,
 * den nächsten neuen Weg wieder zu vergessen. Hier kommt jeder von ihnen
 * vorbei: supabase-js reicht `global.fetch` an PostgREST (Tabellen UND rpc),
 * Storage und Functions durch.
 *
 * Drei Zusagen:
 *   * Der Rumpf wird NUR bei Status 400 gelesen — der einzige Status, mit dem
 *     PostgREST eine `raise exception` ausliefert. Ein 200 oder 500 wird nie
 *     angefasst.
 *   * Gelesen wird ein `clone()`; die ursprüngliche Antwort geht UNVERÄNDERT
 *     und ungelesen an den Aufrufer, der seinen Fehler wie gewohnt auswertet.
 *   * Nichts davon darf einen Request brechen: wirft `clone()` oder `text()`
 *     (fremde Fetch-Nachbauten, schon verbrauchter Rumpf), bleibt es beim
 *     stillen Durchreichen.
 */
export const baueRiegelFetch = (basis: FetchAehnlich, melde: () => void): FetchAehnlich =>
    async (input: any, init?: any) => {
        const antwort = await basis(input, init);

        // Der Klon wird SOFORT genommen, noch bevor die Antwort den Aufrufer
        // erreicht: `clone()` geht nur, solange der Rumpf unberuehrt ist.
        let klon: any = null;
        try {
            if (antwort && antwort.status === BUILD_GUARD_STATUS && typeof antwort.clone === 'function') {
                klon = antwort.clone();
            }
        } catch {
            klon = null;
        }

        if (klon) {
            // GELESEN wird abgekoppelt, und gemeldet wird erst recht spaeter.
            // Sonst liefe der Melder — und mit ihm React-Zustand — mitten in
            // die Fehlerbehandlung des Aufrufers hinein: `useOfflineSync`
            // faengt den Fehler, stuft ihn ein und schreibt den Eintrag mit
            // `markSyncFailure` zurueck. Diese Kette laeuft zuerst zu Ende.
            void (async () => {
                try {
                    const rumpf = await klon.text();
                    if (istBuildRiegelAntwort(antwort.status, rumpf)) {
                        queueMicrotask(melde);
                    }
                } catch {
                    // Ein nicht lesbarer Rumpf ist kein Grund, etwas zu aendern.
                }
            })();
        }

        return antwort;
    };

/**
 * Die Hülle um den globalen `fetch`.
 *
 * `fetch` wird bewusst ohne `this` gerufen (Pfeilfunktion statt einer
 * abgelegten Referenz) — dieselbe Vorsichtsmassnahme, die postgrest-js in
 * `PostgrestBuilder` trifft, sonst quittiert der Browser eine losgelöste
 * Methode mit „Illegal invocation".
 */
const riegelFetch: FetchAehnlich = baueRiegelFetch(
    (input, init) => fetch(input, init),
    meldeBuildRiegel,
);

const notConfiguredError = () =>
    new Error("Supabase is not configured. Missing VITE_SUPABASE_URL/KEY.");

/**
 * Builds a thenable, infinitely chainable query stub. Any builder method
 * (.select, .eq, .single, ...) returns another stub, and awaiting anywhere in
 * the chain rejects with a clear configuration error instead of throwing a
 * confusing "x.eq is not a function" TypeError.
 */
const createRejectingQuery = (): any => {
    const rejection = Promise.reject(notConfiguredError());
    // Suppress unhandled-rejection noise until the chain is actually awaited.
    rejection.catch(() => {});
    return new Proxy(rejection, {
        get(target, prop) {
            if (prop === 'then' || prop === 'catch' || prop === 'finally') {
                return (target as any)[prop].bind(target);
            }
            return () => createRejectingQuery();
        }
    });
};

export const getSupabase = () => {
    if (supabaseInstance) return supabaseInstance;

    const supabaseUrl = (typeof import.meta !== 'undefined' && (import.meta as any).env) 
        ? (import.meta as any).env.VITE_SUPABASE_URL || (process.env as any).VITE_SUPABASE_URL
        : (process.env as any).VITE_SUPABASE_URL;
    const supabaseKey = (typeof import.meta !== 'undefined' && (import.meta as any).env)
        ? (import.meta as any).env.VITE_SUPABASE_KEY || (import.meta as any).env.VITE_SUPABASE_ANON_KEY || (process.env as any).VITE_SUPABASE_KEY
        : (process.env as any).VITE_SUPABASE_KEY;

    if (!supabaseUrl || !supabaseKey) {
        console.error("Supabase configuration missing!");
        if (!supabaseUrl) console.error("Missing: VITE_SUPABASE_URL (check if it is set in Vercel)");
        if (!supabaseKey) console.error("Missing: VITE_SUPABASE_KEY / ANON_KEY (check if it is set in Vercel)");
        // Return a safe mock object to prevent crash on startup. Query chains
        // reject with a clear error when awaited; storage/channel are no-ops.
        return {
            from: () => createRejectingQuery(),
            rpc: () => createRejectingQuery(),
            storage: {
                from: () => ({
                    upload: () => Promise.reject(notConfiguredError()),
                    remove: () => Promise.reject(notConfiguredError()),
                    getPublicUrl: () => ({ data: { publicUrl: '' } }),
                }),
            },
            channel: () => {
                const stub: any = {
                    on: () => stub,
                    subscribe: () => stub,
                    unsubscribe: () => {},
                };
                return stub;
            },
            removeChannel: () => {},
            auth: {
                getSession: () => Promise.resolve({ data: { session: null }, error: null }),
                onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } }),
                getUser: () => Promise.resolve({ data: { user: null }, error: null }),
                signInWithPassword: () => Promise.reject(notConfiguredError()),
                signOut: () => Promise.resolve({ error: null }),
            }
        };
    }

    supabaseInstance = createClient(supabaseUrl, supabaseKey, {
        auth: {
            persistSession: true,
            autoRefreshToken: true,
            detectSessionInUrl: true
        },
        // `global.headers` haengt an JEDEM Weg dieses Clients: supabase-js
        // legt sie einmal auf `this.headers` und reicht sie an PostgREST
        // (Tabellen UND rpc), Storage, Functions und Realtime durch
        // (SupabaseClient.js:59/73/95). Ein unbekannter Header ist fuer
        // PostgREST harmlos — ohne Migration Nr. 112 liest ihn niemand,
        // der Client laeuft unveraendert weiter.
        global: {
            headers: {
                'X-Client-Info': CLIENT_INFO_HEADER_VALUE,
                [APP_BUILD_HEADER]: aktuellerBuildStempel,
            },
            // Der Rueckkanal: erkennt die Abweisung des Riegels auf JEDEM Weg
            // dieses Clients, ohne dass ein Schreibpfad davon wissen muss.
            fetch: riegelFetch,
        }
    });

    return supabaseInstance;
};

// Proxied export to maintain compatibility with existing code while allowing lazy initialization
export const supabase = new Proxy({}, {
    get: (target, prop) => {
        const client = getSupabase();
        const value = (client as any)[prop];
        return typeof value === 'function' ? value.bind(client) : value;
    }
}) as any;
